import { Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { FinancingHold, FinancingHoldScope, Invoice, PlaceFinancingHoldRequest } from 'src/app/api/models';
import { FinancingHoldsService } from 'src/app/api/services';
import { refusalMessage } from 'src/app/shared/financing-holds/financing-hold-text';

export interface PlaceFinancingHoldDialogData {
  invoice: Invoice;
}

interface HoldTarget {
  scope: FinancingHoldScope;
  label: string;
  // "already on hold", with the count above one, when an active hold of this
  // scope applies to the invoice; null otherwise.
  heldMark: string | null;
}

/**
 * PlaceFinancingHoldDialogComponent places financing holds from the invoice
 * page (add-financing-holds, design D13;
 * place-and-release-several-financing-holds, design D5). The targets are
 * this invoice, its client, and its end customer when it has one, each a
 * checkbox: any combination, none ticked at first, at least one required.
 * The reason is required and shared by the holds.
 *
 * One request carries every ticked target, so the server places all of the
 * holds or none.
 *
 * A target is marked "already on hold" when the invoice's `financing_holds`
 * carries a hold of that scope, with the count above one. For the end
 * customer that includes a hold on a twin row, which applies to this end
 * customer's invoices too. The mark informs only: the target stays
 * selectable, and ticking it places another hold alongside.
 *
 * The dialog posts the holds itself, so a refusal is shown inside it and the
 * dialog stays open, with the ticks and the reason, for a correction. It
 * closes with the placed holds, the array the endpoint answers with also for
 * one target (design D2), and with `undefined` on cancel, escape or a
 * backdrop click.
 *
 * The disabled submit is a convenience only: the server trims the reason and
 * refuses a blank one with `422 financing_hold_reason_required`, and a
 * request without a target with `422 financing_hold_invalid_target`.
 */
@Component({
  selector: 'app-place-financing-hold-dialog',
  templateUrl: './place-financing-hold-dialog.component.html',
  styleUrls: ['./place-financing-hold-dialog.component.scss'],
})
export class PlaceFinancingHoldDialogComponent {
  public readonly targets: HoldTarget[];
  // One boolean control per offered target, named by its scope.
  public readonly form: UntypedFormGroup;
  public saving = false;
  public errorMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PlaceFinancingHoldDialogComponent, FinancingHold[]>,
    @Inject(MAT_DIALOG_DATA) public data: PlaceFinancingHoldDialogData,
    private financingHoldsService: FinancingHoldsService,
  ) {
    const invoice = data.invoice;
    this.targets = [
      this.target(FinancingHoldScope.Invoice, `This invoice (${invoice.number || invoice.id})`),
      this.target(FinancingHoldScope.Client, `The client: ${invoice.client?.name ?? invoice.client_id}`),
    ];
    if (invoice.end_customer_id != null) {
      this.targets.push(
        this.target(
          FinancingHoldScope.EndCustomer,
          `The end customer: ${invoice.end_customer?.name ?? invoice.end_customer_id}`,
        ),
      );
    }
    const ticks: Record<string, UntypedFormControl> = {};
    for (const target of this.targets) {
      ticks[target.scope] = new UntypedFormControl(false);
    }
    this.form = new UntypedFormGroup({
      targets: new UntypedFormGroup(ticks),
      reason: new UntypedFormControl(''),
    });
  }

  // The trimmed reason both gates the submit and is what is sent.
  public get reason(): string {
    return ((this.form.value.reason as string | null) ?? '').trim();
  }

  /** The scopes of the ticked targets. */
  public get selectedScopes(): FinancingHoldScope[] {
    const ticks = (this.form.value.targets ?? {}) as Record<string, boolean>;
    return this.targets.filter((target) => ticks[target.scope] === true).map((target) => target.scope);
  }

  // The submit says how many holds it places: "Place hold", "Place 3 holds".
  public get submitLabel(): string {
    const count = this.selectedScopes.length;
    return count > 1 ? `Place ${count} holds` : 'Place hold';
  }

  public get canSubmit(): boolean {
    return !this.saving && this.selectedScopes.length > 0 && this.reason !== '';
  }

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    this.financingHoldsService
      .placeFinancingHold({ body: this.request(this.selectedScopes) })
      // A 403 completes without a value: the HTTP error interceptor shows
      // its own dialog. Re-enable the submit either way.
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (holds: FinancingHold[]) => this.dialogRef.close(holds),
        error: (err: unknown) => {
          this.errorMessage = refusalMessage(err, 'Could not place the hold. Please try again.');
        },
      });
  }

  public cancel(): void {
    this.dialogRef.close(undefined);
  }

  private target(scope: FinancingHoldScope, label: string): HoldTarget {
    const held = (this.data.invoice.financing_holds ?? []).filter((hold) => hold.scope === scope).length;
    return {
      scope,
      label,
      heldMark: held === 0 ? null : held === 1 ? 'already on hold' : `already on hold (${held})`,
    };
  }

  // One request names every ticked target (design D5).
  private request(scopes: FinancingHoldScope[]): PlaceFinancingHoldRequest {
    const invoice = this.data.invoice;
    const request: PlaceFinancingHoldRequest = { reason: this.reason };
    if (scopes.includes(FinancingHoldScope.Client)) {
      request.client_id = invoice.client_id;
    }
    if (scopes.includes(FinancingHoldScope.EndCustomer)) {
      request.end_customer_id = invoice.end_customer_id;
    }
    if (scopes.includes(FinancingHoldScope.Invoice)) {
      request.invoice_id = invoice.id;
    }
    return request;
  }
}
